import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import { resetPassword, resetResetPassword } from '../Store/StudentSlices/resetPasswordSlice';
import { changePassword, resetChangePassword } from '../Store/StudentSlices/changePasswordSlice';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const { loading, error, errorMessage, success } = useAppSelector(
    (state) => state.resetPassword
  );
  const {
    loading: changeLoading,
    error: changeError,
    errorMessage: changeErrorMessage,
    success: changeSuccess,
  } = useAppSelector((state) => state.changePassword);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') ?? '';
  }, [location.search]);

  const firstLoginState = (location.state as { firstLogin?: boolean; oldPassword?: string } | null) ?? null;
  const isFirstLoginFlow = Boolean(firstLoginState?.firstLogin);

  const rules = useMemo(() => {
    return {
      minLength: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+{}[\]:;<>,.?~\\/-]/.test(password),
    };
  }, [password]);

  const isValid =
    rules.minLength &&
    rules.lowercase &&
    rules.uppercase &&
    rules.number &&
    rules.special;

  useEffect(() => {
    if (success || changeSuccess) {
      setIsSuccess(true);
    }
  }, [success, changeSuccess]);

  useEffect(() => {
    return () => {
      dispatch(resetResetPassword());
      dispatch(resetChangePassword());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    if (isFirstLoginFlow) {
      if (!firstLoginState?.oldPassword) {
        setLocalError('Missing temporary password. Please log in again.');
        return;
      }
    } else if (!token) {
      setLocalError('Missing reset token. Please use the link from your email.');
      return;
    }
    setLocalError('');
    if (isFirstLoginFlow) {
      const result = await dispatch(
        changePassword({
          oldPassword: firstLoginState?.oldPassword ?? '',
          newPassword: password,
        })
      );
      if (changePassword.fulfilled.match(result)) {
        setIsSuccess(true);
      }
      return;
    }

    const result = await dispatch(resetPassword({ token, newPassword: password }));
    if (resetPassword.fulfilled.match(result)) {
      setIsSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-10 flex flex-col">
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-between">
        <div className="text-center mt-2">
          <h1 className="text-lg font-semibold text-gray-900">Onefinnet Coaching</h1>
          <p className="text-xs text-gray-500">Expert coaching, personalized growth</p>
        </div>

        <div className="mt-12">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 text-white">
                <svg
                  className="h-7 w-7"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900">Password Changed</h2>
              <p className="mt-2 text-xs text-gray-500">
                Keep this password confidential and avoid sharing it with others.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (isFirstLoginFlow) {
                      navigate('/onboarding', { replace: true });
                    } else {
                      navigate('/login', { replace: true });
                    }
                  }}
                  className="px-8 py-2.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-center">
                <h2 className="text-base font-semibold text-gray-900">
                  {isFirstLoginFlow ? 'Set Your Password' : 'Change your password first'}
                </h2>
                <p className="mt-2 text-xs text-gray-500">
                  {isFirstLoginFlow 
                    ? 'Create a secure password to protect your account'
                    : 'Take control of your password management for enhanced security.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                {(localError || (error && errorMessage) || (changeError && changeErrorMessage)) && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {localError || changeErrorMessage || errorMessage}
                  </div>
                )}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 pr-10"
                      placeholder="Type a strong password"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {showPassword ? (
                          <path d="M3 3l18 18M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58M9.9 4.24A9.53 9.53 0 0112 4c5 0 9.27 3.11 11 8-1.09 2.9-3.11 5.14-5.7 6.5M6.11 6.11C4.39 7.38 3.07 9.12 2 12c1.73 4.89 6 8 10 8 1.05 0 2.06-.17 3-.49" />
                        ) : (
                          <path d="M1.5 12s4.5-7 10.5-7 10.5 7 10.5 7-4.5 7-10.5 7S1.5 12 1.5 12z" />
                        )}
                        {!showPassword && <circle cx="12" cy="12" r="3" />}
                      </svg>
                    </button>
                  </div>

                  {password.length > 0 && (
                    <div className="mt-4 w-full rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:absolute md:left-full md:top-0 md:ml-4 md:mt-0 md:w-52">
                      <p className="text-[11px] font-semibold text-gray-700 mb-2">
                        Password must have:
                      </p>
                      <ul className="space-y-1 text-[11px]">
                        <li className={rules.minLength ? 'text-emerald-600' : 'text-red-500'}>
                          {rules.minLength ? '✓' : '✕'} At least 8 characters
                        </li>
                        <li className={rules.lowercase ? 'text-emerald-600' : 'text-red-500'}>
                          {rules.lowercase ? '✓' : '✕'} Lowercase character (a-z)
                        </li>
                        <li className={rules.uppercase ? 'text-emerald-600' : 'text-red-500'}>
                          {rules.uppercase ? '✓' : '✕'} Uppercase character (A-Z)
                        </li>
                        <li className={rules.special ? 'text-emerald-600' : 'text-red-500'}>
                          {rules.special ? '✓' : '✕'} Special character (!@#$%)
                        </li>
                        <li className={rules.number ? 'text-emerald-600' : 'text-red-500'}>
                          {rules.number ? '✓' : '✕'} Number (0-9)
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  <button
                    type="submit"
                    disabled={!isValid || loading || changeLoading}
                    className="px-10 py-2.5 rounded-lg bg-gray-300 text-white text-xs font-semibold disabled:opacity-70"
                  >
                    {loading || changeLoading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="mb-4 text-center text-xs text-gray-500">
          If you need help,{' '}
          <button type="button" className="underline text-gray-800">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
